import { AnimatePresence, motion } from 'framer-motion';
import { Bookmark, CalendarClock, ChevronRight, Cloud, Download, HardDrive, Image, Images, LayoutDashboard, LayoutGrid, Link, Palette, Monitor, PanelsTopLeft, Type, User, Users, X, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useSpatialNav } from '../../hooks/useSpatialNav';
import { AccountTab } from './AccountTab';
import { AppearanceTab, LayoutTab } from './AppearanceTab';
import { CalendarTab, NewsTab } from './FeedsTabs';
import { LocationTab } from './LocationTab';
import { CloudStorageTab, LocalSourcesTab, MediaOverview, PublicLinksTab } from './MediaTab';
import { PresetsSection } from './PresetsSection';
import { InstallTab } from './InstallTab';
import { SchedulesTab } from './SchedulesTab';
import { ScreensTab } from './ScreensTab';
import { SharingTab } from './SharingTab';
import { useScreen } from '../../store/screen';
import { SlideshowTab } from './SlideshowTab';
import { FontsTab, WidgetStyleTab } from './StyleTabs';
import { WIDGET_PAGES, type WidgetPageId } from './widgetPages';
import { PlannerTab } from './PlannerTab';
import { PowerBITab } from './PowerBITab';
import { ClockTab, ListsTab, TextTab, WeatherTab, WidgetsOverview } from './WidgetsTab';

interface Tab {
  id: string;
  name: string;
  icon: LucideIcon;
  /** Shown indented under its parent in the menu, while that section is open. */
  parent?: string;
  render: (go: (tab: string) => void) => ReactElement;
}

const WIDGET_RENDER: Record<WidgetPageId, () => ReactElement> = {
  clock: () => <ClockTab />,
  location: () => <LocationTab />,
  weather: () => <WeatherTab />,
  calendar: () => <CalendarTab />,
  lists: () => <ListsTab />,
  planner: () => <PlannerTab />,
  powerbi: () => <PowerBITab />,
  news: () => <NewsTab />,
  text: () => <TextTab />,
};

const TABS: Tab[] = [
  { id: 'appearance', name: 'Appearance', icon: Palette, render: (go) => <AppearanceTab go={go} /> },
  { id: 'layout', name: 'Layout', icon: LayoutDashboard, parent: 'appearance', render: () => <LayoutTab /> },
  { id: 'presets', name: 'Saved layouts', icon: Bookmark, parent: 'appearance', render: () => <PresetsSection /> },
  { id: 'slideshow', name: 'Slideshow', icon: Images, parent: 'appearance', render: () => <SlideshowTab /> },
  { id: 'widget-style', name: 'Widget style', icon: PanelsTopLeft, parent: 'appearance', render: () => <WidgetStyleTab /> },
  { id: 'fonts', name: 'Fonts', icon: Type, parent: 'appearance', render: () => <FontsTab /> },
  { id: 'media', name: 'Media', icon: Image, render: (go) => <MediaOverview go={go} /> },
  { id: 'cloud', name: 'Cloud storage', icon: Cloud, parent: 'media', render: () => <CloudStorageTab /> },
  { id: 'links', name: 'Public links', icon: Link, parent: 'media', render: () => <PublicLinksTab /> },
  { id: 'local', name: 'Local & network', icon: HardDrive, parent: 'media', render: () => <LocalSourcesTab /> },
  { id: 'widgets', name: 'Widgets', icon: LayoutGrid, render: (go) => <WidgetsOverview go={go} /> },
  ...WIDGET_PAGES.map((w) => ({ ...w, parent: 'widgets', render: WIDGET_RENDER[w.id] })),
  { id: 'screens', name: 'Screens', icon: Monitor, render: (go) => <ScreensTab go={go} /> },
  { id: 'schedules', name: 'Schedules', icon: CalendarClock, parent: 'screens', render: () => <SchedulesTab /> },
  { id: 'sharing', name: 'Sharing', icon: Users, render: () => <SharingTab /> },
  { id: 'account', name: 'Account', icon: User, render: () => <AccountTab /> },
  { id: 'install', name: 'Install app', icon: Download, render: () => <InstallTab /> },
];

/** When this screen shows a shared canvas, says whose it is and whether changes here are saved to it. */
function SharedCanvasNotice() {
  const canvas = useScreen((s) => s.canvas);
  if (!canvas) return null;
  return (
    <div className="mb-6 rounded-lg border border-[var(--accent)]/50 bg-[var(--accent)]/10 p-3 text-sm" role="note">
      This screen shows the shared canvas <strong>{canvas.name}</strong>
      {canvas.mine ? '' : ` from ${canvas.ownerEmail}`}.{' '}
      {canvas.edit
        ? 'Changes to its look are saved to it for everyone who uses it; your own calendars, photos and lists stay yours.'
        : 'You can’t edit it, so changes to its look here aren’t saved. Make your own copy under Settings → Sharing to change it.'}
    </div>
  );
}

export function SettingsPanel({ open, onClose, initialTab }: { open: boolean; onClose: () => void; initialTab?: string }) {
  const [tab, setTab] = useState(initialTab ?? 'appearance');
  const panel = useRef<HTMLDivElement>(null);
  useSpatialNav(panel, open);

  useEffect(() => {
    if (open && initialTab) setTab(initialTab);
  }, [open, initialTab]);

  // The parent passes a new onClose each render; keep it in a ref so the effect below runs
  // only when the panel opens. (Re-running it on every render used to yank remote focus
  // back to the menu after each change.)
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Escape = keyboard; GoBack/BrowserBack = TV remote Back button.
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    // Move focus into the panel for remote/keyboard users.
    const t = window.setTimeout(() => panel.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')?.focus(), 50);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = TABS.find((t) => t.id === tab) ?? TABS[0]!;
  const parent = active.parent ? TABS.find((t) => t.id === active.parent) : undefined;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            ref={panel}
            role="dialog"
            aria-modal="true"
            aria-label="Settings"
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl bg-[var(--surface-strong)] text-[var(--fg)] shadow-2xl backdrop-blur-xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            <nav
              className="flex w-16 shrink-0 flex-col gap-1 overflow-y-auto border-r border-[var(--border)] p-2 sm:w-48"
              role="tablist"
              aria-orientation="vertical"
            >
              <div className="mb-3 hidden px-2 pt-2 text-sm font-semibold sm:block">KCL Canvas</div>
              {TABS.filter((t) => !t.parent || t.parent === active.id || t.parent === active.parent).map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={t.id === active.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 rounded-lg px-3 text-left text-sm transition ${
                    t.parent ? 'py-1.5 sm:pl-7' : 'py-2.5'
                  } ${
                    t.id === active.id
                      ? 'bg-[var(--accent)] text-black'
                      : t.parent && active.parent !== t.parent && active.id !== t.parent
                        ? 'opacity-70 hover:bg-[var(--border)] hover:opacity-100'
                        : 'hover:bg-[var(--border)]'
                  }`}
                >
                  <t.icon className={`shrink-0 ${t.parent ? 'h-4 w-4' : 'h-5 w-5'}`} aria-hidden />
                  <span className="hidden sm:inline">{t.name}</span>
                </button>
              ))}
              <div className="mt-auto hidden space-y-3 px-2 pb-2 text-xs sm:block">
                <div className="muted flex gap-2">
                  <a href="/privacy" className="hover:underline">
                    Privacy
                  </a>
                  <span aria-hidden>·</span>
                  <a href="/terms" className="hover:underline">
                    Terms
                  </a>
                </div>
                <a
                  href="https://kcltd.es"
                  target="_blank"
                  rel="noopener"
                  className="flex flex-col items-center gap-1.5 rounded-lg border border-[var(--border)] px-2 py-3 text-center transition hover:bg-[var(--border)]"
                >
                  <img src="/kcl-logo.png" alt="" className="h-10 w-10" />
                  <span className="text-[11px] font-medium leading-snug">
                    Kirkpatrick Consult
                    <br />
                    Limited
                  </span>
                </a>
              </div>
            </nav>
            <div className="min-w-0 flex-1 overflow-y-auto p-6" role="tabpanel">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="flex items-center gap-1.5 text-xl font-semibold">
                  {parent && (
                    <>
                      <button type="button" onClick={() => setTab(parent.id)} className="muted font-normal hover:underline">
                        {parent.name}
                      </button>
                      <ChevronRight className="muted h-5 w-5" aria-hidden />
                    </>
                  )}
                  {active.name}
                </h2>
                <button onClick={onClose} className="rounded-full p-2 hover:bg-[var(--border)]" aria-label="Close settings">
                  <X className="h-5 w-5" aria-hidden />
                </button>
              </div>
              <SharedCanvasNotice />
              {active.render(setTab)}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
